import client from './client';

/**
 * API client for the Admin Roles editor WRITE endpoints (Approval Access WP9b).
 * Consumes the WP6 role-definition CRUD:
 *   POST  /admin/adminEmployeeRoles          (create)
 *   PUT   /admin/adminEmployeeRoles/:id       (update name/active/permission booleans)
 *   PATCH /admin/adminEmployeeRoles/:id/active(soft toggle; blocked while in use)
 *
 * The READ endpoint (GET /admin/adminEmployeeRoles) already lives in
 * ./adminManagement.ts (getAdminEmployeeRoles) — reuse it, do not duplicate.
 *
 * The API maps the body's camelCase permission keys to the writable role columns
 * (db/adminEmployeeRoleUtil.WRITABLE_ROLE_PERMISSION_COLUMNS) and deliberately
 * ignores the deprecated ImportPieces / ViewTaskImportPage / Payroll / HR flags,
 * so we never send them. All mutating calls let axios reject on 4xx; the editor
 * surfaces the server's `message` (getApiErrorMessage) as a blocking ErrorBanner.
 */

/** The permission booleans a role create/update may write (camelCase; mirrors
 *  WRITABLE_ROLE_PERMISSION_COLUMNS in the API). */
export type RolePermissionKey =
  | 'admin' | 'supervisor'
  | 'viewTimesheets' | 'approveTimesheets' | 'payrollApproveTimesheets'
  | 'unlockTimesheet' | 'unlockApprovedTimesheet'
  | 'viewTimeOffRequests' | 'approveDenyTimeOffRequests'
  | 'viewEmployees' | 'viewAccountCodes' | 'managePayPeriods' | 'manageLocations'
  | 'viewReports' | 'viewMiscTasks' | 'viewTimesheetComments' | 'viewModifiedDuty'
  | 'processPayroll'
  | 'importTasks' | 'exportAccounting' | 'exportPayroll'
  | 'viewAllEmployees' | 'viewProjectWork'
  | 'viewWorkOrders' | 'manageWorkOrders' | 'manageWorkOrderRates' | 'manageCompanyPhones'
  | 'editEmployeeRole' | 'editMiscTaskAccountCodeAndDescription'
  | 'viewProjectMap' | 'editProjectMap' | 'viewAllProjectsMap'
  | 'viewEmployeeEnrollmentPhotos' | 'editEmployeeEnrollmentPhotos'
  | 'viewProjectsPage' | 'viewGpsExceptions' | 'viewSelfieExceptions'
  | 'activateInactivateEmployees'
  | 'editTimesheetEntries' | 'submitTimesheetForEmployee' | 'editApprovedTimesheet'
  // NPM reds two-level approval (TAS-358). Adding a key here makes every role
  // create/update body require it — RolesTab builds the body from ALL_PERM_KEYS,
  // so both must be added to a PermDef group at the same time.
  | 'approveMiscTasks' | 'overrideMiscTaskApproval'
  // Change Supervisor — per-timesheet, per-pay-period supervisor reassignment.
  | 'reassignTimesheetSupervisor'
  // Time off: correct an already-approved request, and submit one on an employee's behalf.
  | 'editApprovedTimeOffRequests' | 'submitTimeOffForEmployee'
  // Company-wide push reminder to unsubmitted employees, and the weekly safety message page
  // (view + edit). Both replaced super-admin-only gates.
  | 'sendTimesheetReminders' | 'manageSafetyMessages'
  // Kiosk Devices page (TAS-585).
  | 'manageKioskDevices'
  // NPM LA Sick page (TIMESHEET_PROCESSING NPM only; the toggle renders only there).
  | 'viewLASick'
  // Employee Roles page create/edit (TAS-667) — /admin/roles POST/PUT.
  | 'manageEmployeeRoles';

/** Body for POST / PUT /admin/adminEmployeeRoles. */
export type AdminRoleWriteInput = { name: string; active: boolean } & Record<RolePermissionKey, boolean>;

/** POST /admin/adminEmployeeRoles — create a role. Returns the new adminEmployeeRoleID. */
export const createAdminRole = async (input: AdminRoleWriteInput): Promise<number> => {
  const response = await client.post('/admin/adminEmployeeRoles', input);
  return response.data?.adminEmployeeRoleID as number;
};

/** PUT /admin/adminEmployeeRoles/:id — update a role. */
export const updateAdminRole = async (
  adminEmployeeRoleID: number,
  input: AdminRoleWriteInput,
): Promise<void> => {
  await client.put(`/admin/adminEmployeeRoles/${adminEmployeeRoleID}`, input);
};

/** PATCH /admin/adminEmployeeRoles/:id/active — soft deactivate / reactivate. */
export const setAdminRoleActive = async (
  adminEmployeeRoleID: number,
  active: boolean,
): Promise<void> => {
  await client.patch(`/admin/adminEmployeeRoles/${adminEmployeeRoleID}/active`, { active });
};
